import { useEffect, useMemo, useState, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Box, Typography, Stack, TextField, MenuItem, IconButton } from "@mui/material";
import { ArrowLeftRight } from "lucide-react";

// 指標映射表
const indicatorMap = {
  0: "台灣加權指數",
  1: "標普500",
  2: "歐洲斯托克50",
  3: "WTI原油",
  4: "黃金",
  5: "日經225",
  6: "10年期美債",
  7: "DXY美元指數",
  8: "VIX恐慌指數",
  9: "道瓊指數",
};

const colors = [
  "#4CAF50", "#f82015ff", "#9C27B0", "#2196F3", "#C9A700",
  "#f7931b", "#005169ff", "#216900ff", "#510053ff", "#5e5700ff"
];

// 日期格式化
const formatDateToString = (date) => {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
};
const parseDate = (str) => new Date(str);

// 线性插值函数
const interpolateValue = (prevValue, nextValue, prevDate, nextDate, currentDate) => {
  if (prevValue === undefined || nextValue === undefined) return undefined;
  
  const prevTime = new Date(prevDate).getTime();
  const nextTime = new Date(nextDate).getTime();
  const currentTime = new Date(currentDate).getTime();
  
  if (nextTime === prevTime) return prevValue;
  
  const ratio = (currentTime - prevTime) / (nextTime - prevTime);
  return prevValue + (nextValue - prevValue) * ratio;
};

// 数据插补函数
const interpolateData = (data, indicators) => {
  if (!data || data.length === 0) return data;
  
  const sortedData = [...data].sort((a, b) => a.date.localeCompare(b.date));
  const interpolatedData = [];
  
  for (let i = 0; i < sortedData.length; i++) {
    const currentItem = { ...sortedData[i] };
    
    indicators.forEach(indicator => {
      if (currentItem[indicator] === undefined || currentItem[indicator] === null) {
        let prevValue = undefined;
        let prevDate = undefined;
        for (let j = i - 1; j >= 0; j--) {
          if (sortedData[j][indicator] !== undefined && sortedData[j][indicator] !== null) {
            prevValue = sortedData[j][indicator];
            prevDate = sortedData[j].date;
            break;
          }
        }
        
        let nextValue = undefined;
        let nextDate = undefined;
        for (let j = i + 1; j < sortedData.length; j++) {
          if (sortedData[j][indicator] !== undefined && sortedData[j][indicator] !== null) {
            nextValue = sortedData[j][indicator];
            nextDate = sortedData[j].date;
            break;
          }
        }
        
        if (prevValue !== undefined && nextValue !== undefined) {
          currentItem[indicator] = interpolateValue(
            prevValue, nextValue, prevDate, nextDate, currentItem.date
          );
        } else if (prevValue !== undefined) {
          currentItem[indicator] = prevValue;
        } else if (nextValue !== undefined) {
          currentItem[indicator] = nextValue;
        }
      }
    });
    
    interpolatedData.push(currentItem);
  }
  
  return interpolatedData;
};

const DualAxisChart = ({ 
  data, 
  filteredDates, 
  onFreecodeChange,
  initialLeftAxis = 0,
  initialRightAxis = 1,
  controlledLeftAxis,
  controlledRightAxis,
  onAxisChange
}) => {
  const [leftAxis, setLeftAxis] = useState(initialLeftAxis);
  const [rightAxis, setRightAxis] = useState(initialRightAxis);

  const currentLeftAxis = controlledLeftAxis !== undefined ? controlledLeftAxis : leftAxis;
  const currentRightAxis = controlledRightAxis !== undefined ? controlledRightAxis : rightAxis;

  const leftAxisLabel = indicatorMap[currentLeftAxis];
  const rightAxisLabel = indicatorMap[currentRightAxis];

  const freecode = useMemo(() => `1${currentLeftAxis}${currentRightAxis}`, [currentLeftAxis, currentRightAxis]);

  // 改进的数据转换逻辑 - 增加更多调试和容错
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      console.log('[DualAxisChart] No data available');
      return [];
    }

    const firstItem = data[0];
    
    // 检查数据格式
    const isObjectFormat = firstItem && typeof firstItem === 'object';
    const hasNameValue = firstItem && 'name' in firstItem && 'value' in firstItem;
    const hasDate = firstItem && 'date' in firstItem;
    
    // 如果数据已经是正确格式 (包含 date 和指标字段)
    if (isObjectFormat && hasDate && !hasNameValue) {
      // 验证是否包含所需的指标
      const hasLeftIndicator = leftAxisLabel in firstItem;
      const hasRightIndicator = rightAxisLabel in firstItem;
      
      if (hasLeftIndicator || hasRightIndicator) {
        console.log('[DualAxisChart] Using data as-is');
        return data;
      }
    }
    
    // 如果数据是 {date, name, value} 格式,需要转换
    if (hasNameValue && hasDate) {
      const groupedData = {};
      
      data.forEach(item => {
        const date = item.date;
        if (!groupedData[date]) {
          groupedData[date] = { date };
        }
        groupedData[date][item.name] = item.value;
      });
      
      const converted = Object.values(groupedData).sort((a, b) => a.date.localeCompare(b.date));
      return converted;
    }
    
    console.log('[DualAxisChart] Using data as-is (fallback)');
    return data;
  }, [data, leftAxisLabel, rightAxisLabel]);

  // 从数据中提取日期范围
  const { minDate, maxDate } = useMemo(() => {
    let dates = filteredDates;
    
    if (!dates || dates.length === 0) {
      if (chartData && chartData.length > 0) {
        dates = [...new Set(chartData.map(item => item.date))].sort();
      } else {
        dates = [];
      }
    }
    
    if (dates.length === 0) {
      const today = new Date();
      console.log('[DualAxisChart] No dates, using today');
      return { minDate: today, maxDate: today };
    }
    
    const result = {
      minDate: parseDate(dates[0]),
      maxDate: parseDate(dates[dates.length - 1]),
    };
    console.log('[DualAxisChart] Date range:', result);
    return result;
  }, [chartData, filteredDates]);

  const [startDate, setStartDate] = useState(minDate);
  const [endDate, setEndDate] = useState(maxDate);

  useEffect(() => {
    setStartDate(minDate);
    setEndDate(maxDate);
  }, [minDate, maxDate]);

  // 过滤日期范围并进行数据插补
  const displayData = useMemo(() => {
    if (!chartData.length) {
      console.log('[DualAxisChart] No chart data for display');
      return [];
    }
    
    const startStr = formatDateToString(startDate);
    const endStr = formatDateToString(endDate);
    
    console.log('[DualAxisChart] Filtering date range:', { startStr, endStr });
    
    const filtered = chartData.filter(item => {
      const itemDate = item.date;
      return itemDate >= startStr && itemDate <= endStr;
    });
    
    console.log('[DualAxisChart] Filtered data count:', filtered.length);
    
    // 对过滤后的数据进行插补
    const indicators = [leftAxisLabel, rightAxisLabel];
    const interpolated = interpolateData(filtered, indicators);
    
    console.log('[DualAxisChart] Interpolated data:', interpolated.slice(0, 2));
    return interpolated;
  }, [chartData, startDate, endDate, leftAxisLabel, rightAxisLabel]);

  // 通知父组件
  useEffect(() => {
    if (onFreecodeChange) {
      onFreecodeChange(freecode);
    }
  }, [freecode, onFreecodeChange]);

  // 事件处理
  const handleStartDateChange = useCallback((e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const newDate = parseDate(dateValue);
      const endDateStr = formatDateToString(endDate);
      if (dateValue <= endDateStr && dateValue >= formatDateToString(minDate)) {
        setStartDate(newDate);
      }
    }
  }, [endDate, minDate]);

  const handleEndDateChange = useCallback((e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const newDate = parseDate(dateValue);
      const startDateStr = formatDateToString(startDate);
      if (dateValue >= startDateStr && dateValue <= formatDateToString(maxDate)) {
        setEndDate(newDate);
      }
    }
  }, [startDate, maxDate]);

  const handleLeftAxisChange = useCallback((e) => {
    const val = Number(e.target.value);
    if (val !== currentRightAxis) {
      if (onAxisChange) {
        onAxisChange('left', val);
      } else {
        setLeftAxis(val);
      }
    }
  }, [currentRightAxis, onAxisChange]);

  const handleRightAxisChange = useCallback((e) => {
    const val = Number(e.target.value);
    if (val !== currentLeftAxis) {
      if (onAxisChange) {
        onAxisChange('right', val);
      } else {
        setRightAxis(val);
      }
    }
  }, [currentLeftAxis, onAxisChange]);

  // 对调左右轴
  const handleSwapAxes = useCallback(() => {
    const tempLeft = currentLeftAxis;
    const tempRight = currentRightAxis;
    
    console.log('[DualAxisChart] Swapping axes:', { 
      from: { left: tempLeft, right: tempRight }, 
      to: { left: tempRight, right: tempLeft } 
    });
    
    if (onAxisChange) {
      // 使用 setTimeout 确保两次更新按顺序执行
      onAxisChange('left', tempRight);
      setTimeout(() => {
        onAxisChange('right', tempLeft);
      }, 0);
    } else {
      // 直接交换本地状态
      setLeftAxis(tempRight);
      setRightAxis(tempLeft);
    }
  }, [currentLeftAxis, currentRightAxis, onAxisChange]);

  // 检查数据有效性
  const hasValidData = useMemo(() => {
    const valid = displayData.length > 0 && 
      displayData[0] && 
      displayData[0][leftAxisLabel] !== undefined && 
      displayData[0][rightAxisLabel] !== undefined;
    
    return valid;
  }, [displayData, leftAxisLabel, rightAxisLabel]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* 控制区 */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="space-between" sx={{ mb: 2 }}>
        {/* 日期选择 */}
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField label="開始日期" type="date"
            value={formatDateToString(startDate)}
            onChange={handleStartDateChange}
            size="small" InputLabelProps={{ shrink: true }}
            inputProps={{ min: formatDateToString(minDate), max: formatDateToString(endDate) }}
          />
          <Typography variant="body2">至</Typography>
          <TextField label="結束日期" type="date"
            value={formatDateToString(endDate)}
            onChange={handleEndDateChange}
            size="small" InputLabelProps={{ shrink: true }}
            inputProps={{ min: formatDateToString(startDate), max: formatDateToString(maxDate) }}
          />
        </Stack>

        {/* 轴选择 */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ width: "50%" }}>
          <TextField select label="左軸" value={currentLeftAxis} size="small"
            onChange={handleLeftAxisChange}>
            {Object.entries(indicatorMap).map(([key, label]) => (
              <MenuItem key={key} value={Number(key)} disabled={Number(key) === currentRightAxis}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          
          <IconButton 
            onClick={handleSwapAxes}
            size="small"
            sx={{ 
              color: 'primary.main',
              '&:hover': { 
                backgroundColor: 'primary.light',
                transform: 'rotate(180deg)',
                transition: 'transform 0.3s ease'
              }
            }}
            title="對調左右軸"
          >
            <ArrowLeftRight size={20} />
          </IconButton>
          
          <TextField select label="右軸" value={currentRightAxis} size="small"
            onChange={handleRightAxisChange}>
            {Object.entries(indicatorMap).map(([key, label]) => (
              <MenuItem key={key} value={Number(key)} disabled={Number(key) === currentLeftAxis}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      {/* 主图表 */}
      <Box sx={{ height: 600 }}>
        {hasValidData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={displayData} margin={{ top: 30, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeOpacity={0.7} />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
              <YAxis yAxisId="left" stroke={colors[currentLeftAxis]} 
                label={{ value: leftAxisLabel, angle: -90, position: "insideLeft", fill: colors[currentLeftAxis] }} />
              <YAxis yAxisId="right" orientation="right" stroke={colors[currentRightAxis]} 
                label={{ value: rightAxisLabel, angle: 90, position: "insideRight", fill: colors[currentRightAxis] }} />
              <Tooltip 
                formatter={(value, name) => [
                  typeof value === 'number' ? value.toFixed(3) : value, 
                  name
                ]}
                labelFormatter={(label) => `日期: ${label}`}
              />
              <Legend />
              <Line 
                yAxisId="left" 
                type="monotone" 
                dataKey={leftAxisLabel} 
                stroke={colors[currentLeftAxis]} 
                dot={false} 
                strokeWidth={2}
                connectNulls={true}
              />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey={rightAxisLabel} 
                stroke={colors[currentRightAxis]} 
                dot={false} 
                strokeWidth={2}
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: 'text.secondary'
          }}>
            <Typography variant="h6">
              正在加載數據...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DualAxisChart;